/**
 * Pretty-print currency numbers as strings with currency type
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#currency_2
 */
export type FormatCurrencyOptions = {
  currency?: string;
  compact?: boolean;
  shortCurrency?: boolean;
  fractionDigits?: number;
  minimumSignificantDigits?: number;
  maximumSignificantDigits?: number;
  maximumFractionDigits?: number;
  preserveCompactIntegerDigits?: boolean;
};

export const CURRENCY_FORMAT_OPTS = {
  shortCurrency: true,
  compact: true,
  fractionDigits: 2,
  minimumSignificantDigits: 2,
  maximumSignificantDigits: 2,
  preserveCompactIntegerDigits: true,
} as const satisfies FormatCurrencyOptions;

/**
 * Sign shown instead of the currency symbol, from the tenant's cost_unit_label.
 * One char (Ð, $) prefixes the number, a word (vCores) is appended: "Ð1.2K",
 * "1.2K vCores". Module-level rather than a hook so ~40 formatCurrency call sites
 * stay untouched; set from AppMainWrapper via useApplyCostUnitLabel.
 * Empty = real currency formatting ("$1,234 USD") — what tenants that predate the
 * setting keep, and what any render outside the app shell (Storybook) gets.
 */
let costUnitLabel = "";

export const setCostUnitLabel = (label: string) => {
  costUnitLabel = label;
};

export const formatCurrency = (
  value: number | null | undefined,
  {
    currency = "USD",
    compact = false,
    shortCurrency = false,
    fractionDigits,
    minimumSignificantDigits,
    maximumSignificantDigits,
    maximumFractionDigits,
    preserveCompactIntegerDigits = false,
  }: FormatCurrencyOptions = {}
) => {
  if (value == null || typeof value !== "number" || Number.isNaN(value)) {
    return "";
  }

  let normalizedValue = value;
  if (Object.is(value, -0)) {
    // Normalize -0 to 0. "Object.is" is sensitive to signed zeros
    normalizedValue = 0;
  }

  if (typeof fractionDigits === "number") {
    if (fractionDigits > 0) {
      maximumSignificantDigits ??= fractionDigits;
    }
    maximumFractionDigits ??= fractionDigits;
  }

  if (compact && preserveCompactIntegerDigits && maximumSignificantDigits) {
    const absValue = Math.abs(normalizedValue);
    // Compact notation uses K/M/B/T suffixes (1e3/1e6/1e9/1e12).
    // Within each tier (including the unsuffixed 1..999 tier), integer digits
    // cycle 1→2→3 via modular arithmetic.
    // Beyond T (≥1e15), no higher suffix exists so digits accumulate unbounded.
    const compactIntDigits =
      absValue < 1
        ? 0
        : absValue < 1e15
          ? (Math.floor(Math.log10(absValue)) % 3) + 1
          : Math.floor(Math.log10(absValue / 1e12)) + 1;
    if (compactIntDigits > maximumSignificantDigits) {
      maximumSignificantDigits = compactIntDigits;
      minimumSignificantDigits = Math.min(
        minimumSignificantDigits ?? compactIntDigits,
        compactIntDigits
      );
    }
  }

  if (typeof maximumFractionDigits !== "number") {
    maximumFractionDigits = normalizedValue < 1 ? 2 : normalizedValue < 10 ? 1 : 0; // for small values
  }

  // A one-char label is a sign and goes where "$" went, after any minus: "-Ð1.2K".
  const isSignLabel = costUnitLabel.length === 1;

  const result =
    Intl.NumberFormat("en", {
      style: costUnitLabel ? "decimal" : "currency",
      currency,
      notation: compact ? "compact" : "standard",
      minimumFractionDigits: 0, // remove .00
      maximumFractionDigits,
      maximumSignificantDigits,
      minimumSignificantDigits,
    })
      .format(normalizedValue)
      .replace(/^-?/, (minus) => (isSignLabel ? minus + costUnitLabel : minus)) +
    (costUnitLabel
      ? isSignLabel
        ? ""
        : ` ${costUnitLabel}`
      : shortCurrency
        ? ""
        : ` ${currency.toUpperCase()}`);

  if (result.endsWith(".0") || result.endsWith(",0")) {
    return result.slice(0, -2);
  }

  // Fix negative zeros in result: drop the sign, keep any currency symbol/code or unit label
  if (/^-\D?0(\s|$)/.test(result)) {
    return result.replace("-", "");
  }

  return result;
};
